import { useState, useEffect, useRef } from "react";
import {
  createLocalVideoTrack,
  createLocalAudioTrack,
  LocalVideoTrack,
  LocalAudioTrack,
} from "livekit-client";

interface CustomPreJoinProps {
  onSubmit: (values: {
    username?: string;
    audioEnabled?: boolean;
    videoEnabled?: boolean;
    audioDeviceId?: string | null;
    videoDeviceId?: string | null;
  }) => void;
  defaults?: {
    username?: string;
    audioEnabled?: boolean;
    videoEnabled?: boolean;
  };
  videoResolution?: {
    width: number;
    height: number;
    frameRate?: number;
  };
  joinLabel?: string;
  micLabel?: string;
  camLabel?: string;
}

export function CustomPreJoin({
  onSubmit,
  defaults = {},
  videoResolution,
  joinLabel = "Join Session",
}: CustomPreJoinProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const videoWidth = videoResolution ? videoResolution.width / 2 : 540;
  const videoHeight = videoResolution ? videoResolution.height / 2 : 704;

  const [username] = useState(defaults.username || "donor");
  const [videoEnabled, setVideoEnabled] = useState(
    defaults.videoEnabled ?? true
  );
  const [audioEnabled, setAudioEnabled] = useState(
    defaults.audioEnabled ?? true
  );

  const [videoTrack, setVideoTrack] = useState<LocalVideoTrack | null>(null);
  const [audioTrack, setAudioTrack] = useState<LocalAudioTrack | null>(null);

  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>("");
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>("");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [audioLevel, setAudioLevel] = useState<number>(0);
  const [permissionsGranted, setPermissionsGranted] = useState(false);
  const [permissionError, setPermissionError] = useState<string>("");

  // Request permissions and get devices
  useEffect(() => {
    const requestPermissionsAndGetDevices = async () => {
      try {
        setIsLoading(true);
        setPermissionError("");

        console.log("🔐 Requesting camera and microphone permissions...");

        // Request permissions by accessing media
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });

        console.log("✅ Permissions granted!");

        // Stop the temporary stream immediately
        stream.getTracks().forEach((track) => track.stop());

        // Now enumerate devices (labels will be available after permission)
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoInputs = devices.filter((d) => d.kind === "videoinput");
        // filter out the default microphone because then we always have 2
        const audioInputs = devices.filter(
          (d) =>
            d?.kind === "audioinput" && d?.deviceId && d.deviceId !== "default"
        );

        console.log("📱 Available devices:", {
          video: videoInputs.length,
          audio: audioInputs.length,
        });

        setVideoDevices(videoInputs);
        setAudioDevices(audioInputs);

        if (videoInputs.length > 0) {
          setSelectedVideoDevice(videoInputs[0].deviceId);
        }
        if (audioInputs.length > 0) {
          setSelectedAudioDevice(audioInputs[0].deviceId);
        }

        setPermissionsGranted(true);
        setIsLoading(false);
      } catch (err: any) {
        console.error("❌ Failed to get permissions:", err);
        setPermissionError(
          `Permission denied: ${
            err.message || "Please allow camera and microphone access"
          }`
        );
        setPermissionsGranted(false);
        setIsLoading(false);
      }
    };

    requestPermissionsAndGetDevices();
  }, []);

  // Create video track
  useEffect(() => {
    if (!permissionsGranted || !videoEnabled || !selectedVideoDevice) {
      if (videoTrack) {
        videoTrack.stop();
        setVideoTrack(null);
      }
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const createVideo = async () => {
      try {
        setIsLoading(true);
        setError("");

        if (videoTrack) {
          videoTrack.stop();
        }

        const options: any = {
          deviceId: selectedVideoDevice,
        };

        if (videoResolution) {
          options.resolution = videoResolution;
        }

        const track = await createLocalVideoTrack(options);

        // Debug: Check actual resolution
        const settings = track.mediaStreamTrack.getSettings();
        console.log("Actual video resolution:", {
          width: settings.width,
          height: settings.height,
        });

        if (cancelled) {
          track.stop();
          return;
        }

        setVideoTrack(track);

        if (videoRef.current) {
          track.attach(videoRef.current);
        }

        setIsLoading(false);
      } catch (err: any) {
        console.error("Failed to create video track:", err);
        if (!cancelled) {
          setError(`Camera error: ${err.message}`);
          setIsLoading(false);
        }
      }
    };

    createVideo();

    return () => {
      cancelled = true;
    };
  }, [permissionsGranted, videoEnabled, selectedVideoDevice, videoResolution]);

  // Create audio track
  useEffect(() => {
    if (!permissionsGranted || !audioEnabled || !selectedAudioDevice) {
      if (audioTrack) {
        audioTrack.stop();
        setAudioTrack(null);
      }
      setAudioLevel(0);
      return;
    }

    let cancelled = false;

    const createAudio = async () => {
      try {
        if (audioTrack) {
          audioTrack.stop();
        }

        const track = await createLocalAudioTrack({
          deviceId: selectedAudioDevice,
        });

        if (cancelled) {
          track.stop();
          return;
        }

        setAudioTrack(track);
      } catch (err: any) {
        console.error("Failed to create audio track:", err);
      }
    };

    createAudio();

    return () => {
      cancelled = true;
    };
  }, [permissionsGranted, audioEnabled, selectedAudioDevice]);

  // Audio level monitoring
  useEffect(() => {
    if (!audioTrack || !audioEnabled) {
      setAudioLevel(0);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      return;
    }

    const setupAudioMonitoring = async () => {
      try {
        const mediaStream = new MediaStream([audioTrack.mediaStreamTrack]);
        const audioContext = new AudioContext();
        const source = audioContext.createMediaStreamSource(mediaStream);
        const analyser = audioContext.createAnalyser();

        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.3;
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;

        const dataArray = new Uint8Array(analyser.fftSize);

        const updateLevel = () => {
          if (!analyserRef.current) return;

          analyserRef.current.getByteTimeDomainData(dataArray);

          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            const normalized = (dataArray[i] - 128) / 128;
            sum += normalized * normalized;
          }
          const rms = Math.sqrt(sum / dataArray.length);
          const normalized = Math.min(rms * 5, 1);

          setAudioLevel(normalized);

          animationFrameRef.current = requestAnimationFrame(updateLevel);
        };

        updateLevel();
      } catch (err) {
        console.error("Failed to setup audio monitoring:", err);
      }
    };

    setupAudioMonitoring();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [audioTrack, audioEnabled]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      videoTrack?.stop();
      audioTrack?.stop();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const handleSubmit = () => {
    onSubmit({
      username,
      audioEnabled,
      videoEnabled,
      audioDeviceId: selectedAudioDevice || null,
      videoDeviceId: selectedVideoDevice || null,
    });

    videoTrack?.stop();
    audioTrack?.stop();
  };

  // Show permission error state
  if (permissionError) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1.5rem",
          padding: "2rem",
          maxWidth: "600px",
          margin: "0 auto",
          textAlign: "center",
          minHeight: "400px",
        }}
      >
        <div
          style={{
            fontSize: "4rem",
          }}
        >
          🔒
        </div>
        <h2 style={{ color: "white", margin: 0 }}>
          Camera and Microphone Access Required
        </h2>
        <p style={{ color: "rgba(255, 255, 255, 0.7)", margin: 0 }}>
          {permissionError}
        </p>
        <p style={{ color: "rgba(255, 255, 255, 0.6)", fontSize: "0.9rem" }}>
          Please allow access to your camera and microphone in your browser
          settings and refresh the page.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: "1rem 2rem",
            borderRadius: "8px",
            border: "none",
            background: "rgba(147, 51, 234, 0.8)",
            color: "white",
            fontSize: "1rem",
            fontWeight: "600",
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(147, 51, 234, 0.3)",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // Show loading state while requesting permissions
  if (!permissionsGranted) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1.5rem",
          padding: "2rem",
          minHeight: "400px",
        }}
      >
        <div
          style={{
            fontSize: "4rem",
          }}
        >
          <img src="/camera-on.svg" alt="" style={{ filter: "invert(100%)" }} />
        </div>
        <p style={{ color: "white", fontSize: "1.2rem" }}>
          Requesting camera and microphone access...
        </p>
        <p style={{ color: "rgba(255, 255, 255, 0.6)", fontSize: "0.9rem" }}>
          Please allow access in your browser
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "1.5rem",
        padding: "2rem",
        maxWidth: "80vw",
        margin: "0 auto",
      }}
    >
      {/* Video */}
      <div
        style={{
          position: "relative",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {videoEnabled ? (
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            style={{
              width: `${videoWidth}px`,
              height: `${videoHeight}px`,
              objectFit: "contain",
              background: "#000",
              borderRadius: "12px",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
              transform: "scaleX(-1)",
            }}
          />
        ) : (
          <div
            style={{
              width: `${videoWidth}px`,
              height: `${videoHeight}px`,
              background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: "4rem",
            }}
          >
            <img
              src="/camera-off.svg"
              alt=""
              style={{ filter: "invert(100%)", width: "42px", height: "42px" }}
            />
          </div>
        )}

        {isLoading && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "white",
            }}
          >
            Loading...
          </div>
        )}

        {error && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "#ff6b6b",
              background: "rgba(0,0,0,0.8)",
              padding: "1rem",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            ⚠️ {error}
          </div>
        )}
      </div>

      {/* Controls */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          width: "100%",
          maxWidth: "800px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection:  "row",
            gap: "1rem",
            width: "100%",
          }}
        >
          {/* Camera */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
              flex: 1,
            }}
          >
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button
                onClick={() => setVideoEnabled(!videoEnabled)}
                style={{
                  padding: "0.75rem 1rem",
                  borderRadius: "6px",
                  border: "none",
                  background: "rgba(0, 0, 0, 0.3)",
                  cursor: "pointer",
                  fontSize: "1.2rem",
                }}
              >
                {videoEnabled ? (
                  <img
                    src="/camera-on.svg"
                    alt=""
                    style={{ filter: "invert(100%)" }}
                  />
                ) : (
                  <img
                    src="/camera-off.svg"
                    alt=""
                    style={{ filter: "invert(100%)" }}
                  />
                )}
              </button>
              <select
                value={selectedVideoDevice}
                onChange={(e) => setSelectedVideoDevice(e.target.value)}
                disabled={!videoEnabled}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  borderRadius: "6px",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  background: "rgba(0, 0, 0, 0.3)",
                  color: "white",
                  minWidth: 0,
                }}
              >
                {videoDevices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label ||
                      `Camera ${device.deviceId.substring(0, 5)}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Microphone */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
              flex: 1,
            }}
          >
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button
                onClick={() => setAudioEnabled(!audioEnabled)}
                style={{
                  padding: "0.75rem 1rem",
                  borderRadius: "6px",
                  border: "none",
                  background: "rgba(0, 0, 0, 0.3)",
                  cursor: "pointer",
                  fontSize: "1.2rem",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Audio level indicator */}
                {audioEnabled && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: `${audioLevel * 100}%`,
                      background: "white",
                      transition: "height 0.1s ease",
                      pointerEvents: "none",
                    }}
                  />
                )}
                <span style={{ position: "relative", zIndex: 1 }}>
                  {audioEnabled ? (
                    <img
                      src="/microphone-on.svg"
                      alt=""
                      style={{ filter: "invert(100%)" }}
                    />
                  ) : (
                    <img
                      src="/microphone-off.svg"
                      alt=""
                      style={{ filter: "invert(100%)" }}
                    />
                  )}
                </span>
              </button>
              <select
                value={selectedAudioDevice}
                onChange={(e) => setSelectedAudioDevice(e.target.value)}
                disabled={!audioEnabled}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  borderRadius: "6px",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  background: "rgba(0, 0, 0, 0.3)",
                  color: "white",
                  minWidth: 0,
                }}
              >
                {audioDevices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label ||
                      `Microphone ${device.deviceId.substring(0, 5)}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Join Button */}
        <button
          onClick={handleSubmit}
          disabled={isLoading || !!error}
          style={{
            padding: "1rem 2rem",
            marginTop: "0.5rem",
            borderRadius: "8px",
            border: "none",
            background:
              isLoading || error
                ? "rgba(147, 51, 234, 0.4)"
                : "rgba(147, 51, 234, 0.8)",
            color: "white",
            fontSize: "1.1rem",
            fontWeight: "600",
            cursor: isLoading || error ? "not-allowed" : "pointer",
            boxShadow: "0 4px 12px rgba(147, 51, 234, 0.3)",
          }}
        >
          {joinLabel}
        </button>
      </div>
    </div>
  );
}
